// -----------------------------------------------------------------------------
// Mention-aware chat editor.
//
// A `contenteditable` surface that paints @-mentions as inline, atomic chips
// while you type (Cursor-style) and serialises back to the token string in
// `chatMentions.ts` on read. Atomicity comes from `contenteditable=false` chip
// spans, so backspace removes a whole chip and the caret can't land inside one.
//
// Mention detection scans the text node immediately before the caret for a
// trailing `@query`; selecting a suggestion swaps that run for a chip. The
// picker list itself is `MentionPopover`; this component owns the active-index
// + keyboard wiring so the same editor backs both the composer and inline edit.
// -----------------------------------------------------------------------------

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import classNames from "../../utils/classNames";
import {
  Mention,
  MENTION_CATEGORIES,
  MentionType,
  mentionTypeToPaletteType,
  parseMentions,
  serializeMention,
} from "../../utils/chatMentions";
import { getTypePillClasses } from "../shared/Inputs/SearchPaletteTypePill";
import { IChatUser } from "../../models/Chat";
import useMentionAutocomplete, {
  MentionSuggestion,
} from "../../hooks/useMentionAutocomplete";
import MentionPopover, { MentionRow } from "./MentionPopover";

export interface ChatComposerInputHandle {
  getValue: () => string;
  clear: () => void;
  focus: () => void;
}

interface ChatComposerInputProps {
  /** Changes to this value rebuild the editor from `initialValue` — pass the
   * channel id (composer) or message id (inline edit) so drafts don't leak. */
  resetKey: string;
  /** Serialized body the editor is seeded with (existing tokens become chips). */
  initialValue?: string;
  placeholder?: string;
  usersById: Map<string, IChatUser>;
  meId: string;
  className?: string;
  /** When true (composer), Enter submits and Shift+Enter inserts a newline.
   * When false (inline edit), Enter inserts a newline and Cmd/Ctrl+Enter
   * submits. The mention picker always intercepts Enter while open. */
  submitOnEnter?: boolean;
  onChange?: (serialized: string) => void;
  onSubmit?: () => void;
  onCancel?: () => void;
}

interface MentionContext {
  query: string;
  node: Text;
  atIndex: number;
  endOffset: number;
}

function buildChip(mention: Mention): HTMLSpanElement {
  const chip = document.createElement("span");
  chip.contentEditable = "false";
  chip.dataset.mentionType = mention.type;
  chip.dataset.mentionId = mention.id;
  chip.dataset.mentionLabel = mention.label;
  chip.className = classNames(
    "mx-0.5 inline-flex items-center rounded px-1 py-px text-[0.95em] font-medium align-baseline",
    getTypePillClasses(mentionTypeToPaletteType(mention.type), true)
  );
  chip.textContent = `@${mention.label}`;
  return chip;
}

function isChip(node: Node): node is HTMLElement {
  return (
    node.nodeType === Node.ELEMENT_NODE &&
    (node as HTMLElement).dataset.mentionId !== undefined
  );
}

/** Walks the editor DOM and rebuilds the serialized token string. */
function serializeEditor(root: HTMLElement): string {
  let out = "";

  const walk = (node: Node) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        out += child.textContent ?? "";
      } else if (isChip(child)) {
        out += serializeMention({
          type: child.dataset.mentionType as Mention["type"],
          id: child.dataset.mentionId as string,
          label: child.dataset.mentionLabel as string,
        });
      } else if (child.nodeName === "BR") {
        out += "\n";
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        // contenteditable can wrap lines in <div> blocks; treat each as a line.
        if (out.length > 0 && !out.endsWith("\n")) out += "\n";
        walk(child);
      }
    });
  };

  walk(root);
  return out;
}

/** Renders a serialized body into the editor as text + chip nodes. */
function paintEditor(root: HTMLElement, value: string) {
  root.innerHTML = "";
  for (const segment of parseMentions(value)) {
    if (segment.kind === "text") {
      root.appendChild(document.createTextNode(segment.text));
    } else {
      root.appendChild(buildChip(segment.mention));
    }
  }
}

const ChatComposerInput = forwardRef<
  ChatComposerInputHandle,
  ChatComposerInputProps
>(function ChatComposerInput(
  {
    resetKey,
    initialValue = "",
    placeholder,
    usersById,
    meId,
    className,
    submitOnEnter = true,
    onChange,
    onSubmit,
    onCancel,
  },
  ref
) {
  const { t } = useTranslation("chat");
  const editorRef = useRef<HTMLDivElement>(null);
  const [isEmpty, setIsEmpty] = useState(!initialValue);
  const [mention, setMention] = useState<MentionContext | null>(null);
  const [selectedType, setSelectedType] = useState<MentionType | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  // Identifies the current @ "session" so we only reset the chosen type when a
  // brand-new mention starts, not on every keystroke within the same one.
  const sessionRef = useRef<{ node: Text; atIndex: number } | null>(null);

  const { suggestions, isLoading } = useMentionAutocomplete(
    mention ? mention.query : null,
    selectedType,
    usersById,
    meId
  );

  const closeMention = useCallback(() => {
    sessionRef.current = null;
    setMention(null);
    setSelectedType(null);
    setActiveIndex(0);
  }, []);

  // Stage 1 lists the type folders; stage 2 lists scoped results.
  const rows = useMemo<MentionRow[]>(() => {
    if (!mention) return [];
    if (selectedType === null) {
      return MENTION_CATEGORIES.map((category) => ({
        key: `category-${category.type}`,
        paletteType: mentionTypeToPaletteType(category.type),
        label: category.label,
      }));
    }
    return suggestions.map((suggestion) => ({
      key: `${suggestion.type}-${suggestion.id}`,
      paletteType: mentionTypeToPaletteType(suggestion.type),
      label: suggestion.label,
      description: suggestion.description,
      showTypeTag: true,
    }));
  }, [mention, selectedType, suggestions]);

  // Keep the highlighted row valid as the result set grows/shrinks.
  useEffect(() => {
    setActiveIndex((i) => (i >= rows.length ? 0 : i));
  }, [rows.length]);

  useImperativeHandle(ref, () => ({
    getValue: () =>
      editorRef.current ? serializeEditor(editorRef.current) : "",
    clear: () => {
      if (editorRef.current) editorRef.current.innerHTML = "";
      setIsEmpty(true);
      closeMention();
      onChange?.("");
    },
    focus: () => editorRef.current?.focus(),
  }));

  // Rebuild the editor whenever the conversation / edited message changes.
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    paintEditor(el, initialValue);
    setIsEmpty(!initialValue);
    closeMention();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  const emitChange = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const serialized = serializeEditor(el);
    onChange?.(serialized);
    setIsEmpty(serialized.trim().length === 0);
  }, [onChange]);

  // Inspect the text immediately before the caret for an open `@query`.
  const detectMention = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || !selection.isCollapsed || selection.rangeCount === 0) {
      closeMention();
      return;
    }

    const range = selection.getRangeAt(0);
    const node = range.startContainer;
    if (node.nodeType !== Node.TEXT_NODE || !editorRef.current?.contains(node)) {
      closeMention();
      return;
    }

    const textNode = node as Text;
    const upToCaret = (textNode.textContent ?? "").slice(0, range.startOffset);
    // Trigger on `@` at the start or following whitespace; allow common id
    // characters in the query so partial part/tag numbers still match.
    const match = /(^|\s)@([\w\-./]*)$/.exec(upToCaret);

    if (!match) {
      closeMention();
      return;
    }

    const atIndex = match.index + match[1].length;

    // A different anchor means a fresh @ — drop back to the folder list. Typing
    // within the same anchor keeps the chosen type so the search can continue.
    const previous = sessionRef.current;
    const isNewSession =
      !previous || previous.node !== textNode || previous.atIndex !== atIndex;
    sessionRef.current = { node: textNode, atIndex };

    setMention({
      query: match[2],
      node: textNode,
      atIndex,
      endOffset: range.startOffset,
    });
    if (isNewSession) {
      setSelectedType(null);
      setActiveIndex(0);
    }
  }, [closeMention]);

  const handleInput = useCallback(() => {
    emitChange();
    detectMention();
  }, [emitChange, detectMention]);

  const insertMention = useCallback(
    (suggestion: MentionSuggestion) => {
      const el = editorRef.current;
      if (!el || !mention) return;

      // Replace the `@query` run with a chip + trailing space so the caret can
      // keep typing after it.
      const range = document.createRange();
      range.setStart(mention.node, mention.atIndex);
      range.setEnd(mention.node, mention.endOffset);
      range.deleteContents();

      const chip = buildChip({
        type: suggestion.type,
        id: suggestion.id,
        label: suggestion.label,
      });
      const spacer = document.createTextNode("\u00A0");

      range.insertNode(spacer);
      range.insertNode(chip);

      // Drop the caret just after the trailing space.
      const after = document.createRange();
      after.setStartAfter(spacer);
      after.collapse(true);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(after);

      closeMention();
      emitChange();
      el.focus();
    },
    [mention, emitChange, closeMention]
  );

  // Selects a row: stage 1 (folders) picks a type; stage 2 inserts the chip.
  const pick = useCallback(
    (index: number) => {
      if (!mention) return;
      if (selectedType === null) {
        const category = MENTION_CATEGORIES[index];
        if (!category) return;
        setSelectedType(category.type);
        setActiveIndex(0);
        editorRef.current?.focus();
        return;
      }
      const suggestion = suggestions[index];
      if (suggestion) insertMention(suggestion);
    },
    [mention, selectedType, suggestions, insertMention]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      // Picker navigation takes precedence while it's open.
      if (mention) {
        if (rows.length > 0 && e.key === "ArrowDown") {
          e.preventDefault();
          setActiveIndex((i) => (i + 1) % rows.length);
          return;
        }
        if (rows.length > 0 && e.key === "ArrowUp") {
          e.preventDefault();
          setActiveIndex((i) => (i - 1 + rows.length) % rows.length);
          return;
        }
        if (rows.length > 0 && (e.key === "Enter" || e.key === "Tab")) {
          e.preventDefault();
          pick(activeIndex);
          return;
        }
        if (e.key === "Escape") {
          e.preventDefault();
          // Step back to the folder list before closing the picker entirely.
          if (selectedType !== null) {
            setSelectedType(null);
            setActiveIndex(0);
          } else {
            closeMention();
          }
          return;
        }
      }

      if (e.key === "Escape" && onCancel) {
        e.preventDefault();
        onCancel();
        return;
      }

      if (e.key === "Enter") {
        const wantsSubmit = submitOnEnter
          ? !e.shiftKey
          : e.metaKey || e.ctrlKey;
        if (wantsSubmit) {
          e.preventDefault();
          onSubmit?.();
        }
        // Otherwise fall through and let the browser insert a newline.
      }
    },
    [mention, rows, activeIndex, pick, selectedType, closeMention, onCancel, submitOnEnter, onSubmit]
  );

  // Strip formatting from pasted content — we only store plain text + tokens.
  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLDivElement>) => {
      e.preventDefault();
      const text = e.clipboardData.getData("text/plain");
      document.execCommand("insertText", false, text);
    },
    []
  );

  const showPlaceholder = isEmpty && !!placeholder;
  const selectedCategoryLabel =
    selectedType !== null
      ? MENTION_CATEGORIES.find((c) => c.type === selectedType)?.label
      : undefined;

  return (
    <div className="relative">
      <MentionPopover
        open={!!mention}
        header={selectedCategoryLabel}
        rows={rows}
        activeIndex={activeIndex}
        isLoading={!!mention && isLoading}
        emptyHint={selectedType !== null ? t("No matches") : undefined}
        onHover={setActiveIndex}
        onPick={pick}
      />

      <div className="relative">
        {showPlaceholder ? (
          <span
            className={classNames(
              "pointer-events-none absolute inset-0 select-none text-sm text-gray-400",
              className
            )}
          >
            {placeholder}
          </span>
        ) : null}
        <div
          ref={editorRef}
          role="textbox"
          aria-multiline="true"
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onKeyUp={detectMention}
          onMouseUp={detectMention}
          onPaste={handlePaste}
          onBlur={closeMention}
          className={classNames(
            "max-h-48 w-full overflow-y-auto whitespace-pre-wrap break-words text-sm text-gray-800 focus:outline-none",
            className
          )}
        />
      </div>
    </div>
  );
});

export default ChatComposerInput;
